import { useState, useEffect } from 'react';
import billingService, { type UserSubscriptionStatus } from '../services/billingService';

const ADDON_IDS = {
  individualWebinars: 'Webinars-Module-Yearly',
  individualOnDemand: 'OnDemand-Module-Yearly',
  enterpriseWebinars: 'Enterprise-Webinar-Module-Yearly',
  enterpriseOnDemand: 'Enterprise-OnDemand-Module-Yearly',
};

export interface SubscriptionAccess {
  hasWebinarsAccess: boolean;
  hasOnDemandAccess: boolean;
  loading: boolean;
  status: UserSubscriptionStatus | null;
}

function deriveAccess(status: UserSubscriptionStatus): { hasWebinarsAccess: boolean; hasOnDemandAccess: boolean } {
  if (!status.hasActiveSubscription) {
    return { hasWebinarsAccess: false, hasOnDemandAccess: false };
  }

  if (status.subscriptionSource === 'tenant' && status.tenantSubscription) {
    const addons = status.tenantSubscription.activeAddonIds;
    return {
      hasWebinarsAccess: addons.includes(ADDON_IDS.enterpriseWebinars),
      hasOnDemandAccess: addons.includes(ADDON_IDS.enterpriseOnDemand),
    };
  }

  if (status.subscriptionSource === 'user' && status.userSubscription) {
    const addons = status.userSubscription.activeAddonIds;
    return {
      hasWebinarsAccess: addons.includes(ADDON_IDS.individualWebinars),
      hasOnDemandAccess: addons.includes(ADDON_IDS.individualOnDemand),
    };
  }

  return { hasWebinarsAccess: false, hasOnDemandAccess: false };
}

export function useSubscriptionAccess(): SubscriptionAccess {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<UserSubscriptionStatus | null>(null);

  useEffect(() => {
    billingService.getUserSubscriptionStatus()
      .then(setStatus)
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !status) {
    return { hasWebinarsAccess: false, hasOnDemandAccess: false, loading, status: null };
  }

  return { ...deriveAccess(status), loading: false, status };
}
